/**
 * ChatInputArea.tsx
 *
 * Chat input area component with file attachment, text input, and send button.
 * Extracted from ChatInner.v2.tsx.
 * Now with mobile-responsive styling and touch-friendly targets.
 */

import { FC, useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import Message from "src/app/models/Message";
import { Collaboration } from "src/app/models/Chat";
import { Order } from "src/app/models/Order";
import Language from "src/app/models/Language";
import ChatMessageToReply from "src/app/layout/chat-handle/components/chat-replymessage/ChatMessageToReply";
import { ChatMessageOrderInitiateView } from "src/app/layout/chat-handle/components/chat-orderinitiate/ChatMessageOrderInitiateView";
import { InteractiveMessageList } from "./InteractiveMessageList";
import FileTypeSelector from "./FileTypeSelector";
import ProductPickerModal from "./ProductPickerModal";
import type { Product } from "src/app/modules/product/types/ProductListing";
import selectedProductPerRoom from "../../services/selectedProductPerRoom";
import messageApiClient from "../../services/messageApiClient";
import {
  buildProductRequestBody,
  ProductSendPlatform,
} from "../../services/helpers/productMessageHelper";
import UploadProgressBar from "./UploadProgressBar";
import CharacterCounter from "./CharacterCounter";
import SelectedProductPreview from "./SelectedProductPreview";
import { useResponsive } from "../../../../../shared/forms/hooks/useResponsive";

export interface ChatInputAreaProps {
  // State
  message: string;
  file: File | undefined;
  inputFileName: string;
  fileSize?: string;
  generatedThumbnail: string | null;
  messageType: string | undefined;
  isSending: boolean;
  /**
   * Setter for the parent-owned `isSending` lock. Used by send paths
   * that don't go through `onSendMessage` (e.g. TikTok PRODUCT_CARD) so
   * they participate in the same single-send-in-flight guard instead of
   * inventing a parallel flag.
   */
  setIsSending?: (sending: boolean) => void;
  replyToMessage: Message | "";
  orderInitiateMessage: Order | undefined;

  // Error handling
  sendError?: string;
  canRetry?: boolean;

  // Upload progress
  uploadProgress?: number;
  isUploading?: boolean;
  isSendingToPlatform?: boolean;
  estimatedUploadTime?: string;

  // Collaboration data
  collaboration: Collaboration | undefined;
  companyId: string;
  /** Channel type for file restrictions (e.g., "whatsapp", "shopee") */
  channel?: string;
  /**
   * Marketplace shop ID of the room's account. Same source as the file-upload
   * `sender` (account.marketplaceShopId). Required to scope the product picker
   * to the correct shop.
   */
  shopId?: string;
  /**
   * Buyer (recipient) ID on the marketplace. For TikTok this is the room's
   * `userSearchKey`. Used as the `to` field when sending a PRODUCT_CARD via
   * the unified send endpoint.
   */
  buyerId?: string;

  // Callbacks
  onMessageChange: (value: string) => void;
  onFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: () => void;
  onSendMessage: (templateLanguage?: Language) => void;
  onEnterPress: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onClearReply?: () => void;
  onClearOrder?: () => void;
  onRetry?: () => void;
  onCancelUpload?: () => void;
}

const ChatInputArea: FC<ChatInputAreaProps> = ({
  message,
  file,
  inputFileName,
  fileSize,
  generatedThumbnail,
  messageType,
  isSending,
  setIsSending,
  replyToMessage,
  orderInitiateMessage,
  sendError,
  canRetry,
  uploadProgress,
  isUploading,
  isSendingToPlatform,
  estimatedUploadTime,
  collaboration,
  companyId,
  channel,
  shopId,
  buyerId,
  onMessageChange,
  onFileSelect,
  onRemoveFile,
  onSendMessage,
  onEnterPress,
  onClearReply,
  onClearOrder,
  onRetry,
  onCancelUpload,
}) => {
  const { t } = useTranslation();
  const sendButtonRef = useRef<HTMLButtonElement>(null);
  const chatInputRef = useRef<HTMLTextAreaElement>(null);

  // Mobile responsive detection
  const { isMobile } = useResponsive();

  // Helper function to get file icon based on message type
  const getFileIcon = (type: string | undefined): string => {
    switch (type) {
      case "document":
        return "bi-file-earmark-text";
      case "video":
        return "bi-camera-video";
      case "audio":
        return "bi-music-note-beamed";
      default:
        return "bi-file-earmark";
    }
  };

  // Check if caption is supported for current platform and message type
  const isCaptionSupported = (): boolean => {
    if (messageType !== "image" && messageType !== "video") {
      return true; // Not a media type that needs caption check
    }
    // only WhatsApp and Shopee support captions for images/videos, Lazada and TikTok do not support captions
    return channel === "whatsapp" || channel === "shopee";
  };

  // Focus input on mount
  useEffect(() => {
    if (chatInputRef.current) {
      chatInputRef.current.focus();
    }
  }, []);

  // TikTok product picker visibility + currently-selected product preview.
  // The selected product is shown as a quote-bubble above the input row, the
  // same way `replyToMessage` and `orderInitiateMessage` previews work.
  // Day 3 will hook this into the actual send-message flow.
  const [isProductPickerOpen, setIsProductPickerOpen] = useState(false);

  // Per-room selection memory: a product picked in room A should still be
  // there when the user returns to room A after visiting room B. Keyed by
  // shopId+buyerId so two rooms in the same shop with different buyers
  // don't collide. The picker is closed on any room change.
  const productRoomKey = selectedProductPerRoom.roomKey(shopId, buyerId);
  const [selectedProduct, setSelectedProductState] = useState<Product | null>(
    selectedProductPerRoom.get(productRoomKey)
  );

  useEffect(() => {
    setSelectedProductState(selectedProductPerRoom.get(productRoomKey));
    setIsProductPickerOpen(false);
  }, [productRoomKey]);

  // Write-through wrapper so all selection mutations stay in sync with
  // the per-room store. Callers use this exactly like the original
  // setSelectedProduct setter.
  const setSelectedProduct = (next: Product | null) => {
    setSelectedProductState(next);
    if (next) {
      selectedProductPerRoom.set(productRoomKey, next);
    } else {
      selectedProductPerRoom.clear(productRoomKey);
    }
  };

  const isTiktok = channel === "tiktok";
  const isLazada = channel === "lazada";
  const isShopee = channel === "shopee";
  const canPickProduct = (isTiktok || isLazada || isShopee) && !!shopId;

  // Attaching a file should take precedence over a pending product selection,
  // and on platforms that don't support captions the existing text would only
  // get greyed out — clear it instead so the user isn't left with hidden text
  // that won't be sent.
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (selectedProduct) setSelectedProduct(null);
    if (message && (isTiktok || channel === "lazada")) onMessageChange("");
    onFileSelect(e);
  };

  // Send the selected product via the unified message endpoint. Wire shape
  // is identical across TikTok and Lazada — only `messaging_product` differs
  // (see docs/prd/chat-queue/appendices/api-request-format.md §5.10).
  const sendSelectedProduct = async (): Promise<boolean> => {
    if (!canPickProduct || !selectedProduct || !shopId || !buyerId)
      return false;
    const platform: ProductSendPlatform = isLazada
      ? "lazada"
      : isShopee
      ? "shopee"
      : "tiktok";
    // Re-check the parent-owned lock here too — handleSendClick already
    // guards, but a Promise-y race could double-enter.
    if (isSending) return false;
    setIsSending?.(true);
    try {
      const body = buildProductRequestBody(
        platform,
        buyerId,
        selectedProduct.id
      );
      await messageApiClient.sendMessage(
        platform,
        shopId,
        buyerId,
        // Unified path: the prebuilt body is the source of truth. Pass a
        // minimal stand-in to satisfy the signature.
        // @ts-expect-error — Message arg is structural and unused here
        { messageType: "product", textContent: "" },
        body
      );
      return true;
    } catch (err) {
      console.error("[ChatInputArea] product send failed", err);
      return false;
    } finally {
      // Clear the selection on every send attempt (success OR failure).
      // The failure path is dominated by Lazada-side "item not found" — when
      // a specific buyer conversation can't address the item (delisted,
      // geo-restricted, sold out for that region). Retrying the same item
      // will keep failing, so we get the user unstuck instead of leaving
      // the broken selection in the picker.
      setSelectedProduct(null);
      setIsSending?.(false);
    }
  };

  const handleSendClick = () => {
    if (isSending) return;
    if (canPickProduct && selectedProduct && shopId && buyerId) {
      void sendSelectedProduct();
      return;
    }
    onSendMessage();
  };

  // Detect transparency in image preview so we can warn the user that
  // transparent areas will be flattened (to white) by the receiving platform.
  const [hasTransparency, setHasTransparency] = useState(false);
  useEffect(() => {
    if (!file || messageType !== "image") {
      setHasTransparency(false);
      return;
    }
    // Cheap pre-filter: only formats that can carry an alpha channel
    const mayHaveAlpha =
      file.type === "image/png" ||
      file.type === "image/webp" ||
      file.type === "image/gif" ||
      /\.(png|webp|gif)$/i.test(file.name);
    if (!mayHaveAlpha) {
      setHasTransparency(false);
      return;
    }

    let cancelled = false;
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        const sample = 32; // downsample to keep the alpha scan cheap
        const canvas = document.createElement("canvas");
        canvas.width = sample;
        canvas.height = sample;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        ctx.drawImage(img, 0, 0, sample, sample);
        const data = ctx.getImageData(0, 0, sample, sample).data;
        for (let i = 3; i < data.length; i += 4) {
          if (data[i] < 255) {
            if (!cancelled) setHasTransparency(true);
            return;
          }
        }
        if (!cancelled) setHasTransparency(false);
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      if (!cancelled) setHasTransparency(false);
    };
    img.src = objectUrl;

    return () => {
      cancelled = true;
    };
  }, [file, messageType]);

  // Handle text input change
  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onMessageChange(e.target.value);
  };

  if (channel === 'tokopedia') {
    return <div role="status" className="p-3 text-muted">This historical Tokopedia conversation is read-only.</div>;
  }

  return (
    <div
      className="d-flex flex-column w-100 bg-secondary p-0 m-0 row-gap-1 px-2 py-2"
      data-testid="chat-composer-container"
      style={{
        flexShrink: 0, // Prevent footer from shrinking
        minHeight: "60px",
      }}
      id="kt_chat_messenger_footer"
    >
      {/* Interactive message buttons */}
      {channel === "whatsapp" && (
        <InteractiveMessageList
          companyId={companyId}
          sendMessage={onSendMessage}
          collaboration={collaboration}
        />
      )}

      {/* Error display */}
      {sendError && !isSending && (
        <div
          data-testid="error-message"
          role="alert"
          className="alert alert-danger d-flex align-items-center justify-content-between m-0 mb-2 py-2 px-3"
          style={{ fontSize: "0.875rem" }}
        >
          <div className="d-flex align-items-center">
            <i className="bi bi-exclamation-triangle-fill me-2" />
            <span>{sendError}</span>
          </div>
          {canRetry && onRetry && (
            <button
              data-testid="retry-button"
              type="button"
              className="btn btn-sm btn-light-danger ms-3"
              onClick={onRetry}
            >
              <i className="bi bi-arrow-clockwise me-1" />
              Retry
            </button>
          )}
        </div>
      )}

      {/* Order initiate message */}
      {!replyToMessage && orderInitiateMessage && (
        <div
          id="quoted-order-wrapper"
          data-testid="quoted-order-wrapper"
          className="d-flex flex-row align-items-center fade-in m-0 w-100 py-2 ps-3"
          style={{ backgroundColor: "#F0F3F7" }}
        >
          <div
            id="quoted-order"
            className="d-flex w-100"
            style={{
              margin: "auto",
              width: "100%",
              height: "auto",
              backgroundColor: "#C9C9C9",
              paddingRight: "0px",
              borderRadius: "8px",
              borderLeftWidth: "6px",
              borderLeftStyle: "solid",
              borderLeftColor: "#034CA3",
            }}
          >
            <ChatMessageOrderInitiateView order={orderInitiateMessage} />
          </div>
          <div
            className="d-flex align-items-center mx-3 cursor-pointer"
            style={{ right: 0 }}
          >
            <button
              type="button"
              className="btn btn-icon btn-sm btn-icon btn-active-light-primary m-0 p-0"
              onClick={onClearOrder}
            >
              <div className="d-flex align-items-center mx-3 bi bi-x-lg cursor-pointer" />
            </button>
          </div>
        </div>
      )}

      {/* Reply to message */}
      {replyToMessage !== "" && (
        <div
          id="quoted-reply-wrapper"
          data-testid="quoted-reply-wrapper"
          className="d-flex flex-row align-items-center fade-in m-0 w-100 py-2 ps-3"
        >
          <div
            id="quoted-reply"
            className="d-flex w-100"
            style={{
              margin: "auto",
              width: "100%",
              height: "auto",
              backgroundColor: "#C9C9C9",
              paddingRight: "0px",
              borderRadius: "8px",
              borderLeftWidth: "6px",
              borderLeftStyle: "solid",
              borderLeftColor: "#034CA3",
            }}
          >
            {replyToMessage &&
            (replyToMessage.messageType === "text" ||
              replyToMessage.messageType === "button" ||
              replyToMessage.messageType === "image" ||
              replyToMessage.messageType === "video" ||
              replyToMessage.messageType === "audio" ||
              replyToMessage.messageType === "document" ||
              replyToMessage.messageType === "location" ||
              replyToMessage?.messageType === "interactive") ? (
              <ChatMessageToReply
                messageToReply={replyToMessage}
                customer={collaboration?.customerModel}
                phoneNumber={collaboration?.phoneNumber ?? ""}
              />
            ) : (
              <div className="py-7 px-2" style={{ color: "#034CA3" }}>
                ONPROCESS
              </div>
            )}
          </div>
          <div
            className="d-flex align-items-center mx-3 cursor-pointer"
            style={{ right: 0 }}
          >
            <button
              type="button"
              className="btn btn-icon btn-sm btn-icon btn-active-light-primary m-0 p-0"
              onClick={onClearReply}
            >
              <div className="d-flex align-items-center mx-3 bi bi-x-lg cursor-pointer" />
            </button>
          </div>
        </div>
      )}

      {/* Selected product preview — shown for any product-capable platform
          (TikTok, Lazada) until the user clears or sends. */}
      {canPickProduct && selectedProduct && (
        <SelectedProductPreview
          product={selectedProduct}
          onClear={() => setSelectedProduct(null)}
        />
      )}

      {/* Transparency warning — generic, applies to any channel that flattens alpha */}
      {file && messageType === "image" && hasTransparency && (
        <div
          data-testid="transparency-warning"
          role="alert"
          className="alert alert-warning d-flex align-items-center m-0 mb-2 py-2 px-3"
          style={{ fontSize: "0.875rem" }}
        >
          <i className="bi bi-exclamation-triangle-fill me-2" />
          <span>Transparent areas may appear white after sending.</span>
        </div>
      )}

      {/* Image/Media Preview */}
      {file && generatedThumbnail && messageType === "image" && (
        <div
          id="chat-image-attachment-preview"
          className="chat-composer-preview-row"
          data-testid="image-preview-container"
        >
          <div className="chat-composer-preview-card">
            <img
              src={generatedThumbnail}
              alt={inputFileName}
              className="chat-composer-preview-thumb"
            />
            <div className="chat-composer-preview-content">
              <div className="chat-composer-preview-eyebrow">
                Image selected
              </div>
              <div
                className="chat-composer-preview-title"
                title={inputFileName}
              >
                {inputFileName}
              </div>
              {fileSize && (
                <div className="chat-composer-preview-meta">
                  {fileSize}
                </div>
              )}
            </div>
          </div>
          <button
            type="button"
            className="chat-composer-preview-clear"
            onClick={onRemoveFile}
            title={t("HC.File.RemoveFile")}
            aria-label={t("HC.File.RemoveFile")}
            data-testid="remove-image-button"
          >
            <i className="bi bi-x-lg" />
          </button>
        </div>
      )}

      {/* Non-image file preview (document, audio, video without thumbnail) */}
      {file && !generatedThumbnail && (
        <div
          id="chat-file-attachment-preview"
          className="chat-composer-preview-row"
          data-testid="file-preview-container"
        >
          <div className="chat-composer-preview-card">
            <div className="chat-composer-preview-thumb chat-composer-preview-thumb-icon">
              <i className={`bi ${getFileIcon(messageType)}`} />
            </div>
            <div className="chat-composer-preview-content">
              <div className="chat-composer-preview-eyebrow">
                File selected
              </div>
              <div
                className="chat-composer-preview-title"
                title={inputFileName}
              >
                {inputFileName}
              </div>
              {fileSize && (
                <div className="chat-composer-preview-meta">
                  {fileSize}
                </div>
              )}
            </div>
          </div>
          <button
            type="button"
            className="chat-composer-preview-clear"
            onClick={onRemoveFile}
            title={t("HC.File.RemoveFile")}
            aria-label={t("HC.File.RemoveFile")}
            data-testid="remove-file-button"
          >
            <i className="bi bi-x-lg" />
          </button>
        </div>
      )}

      {/* Upload/Sending progress bar */}
      {(isUploading || isSendingToPlatform) &&
        file &&
        uploadProgress !== undefined && (
          <UploadProgressBar
            progress={uploadProgress}
            fileName={inputFileName}
            fileSize={fileSize || ""}
            mode={isSendingToPlatform ? "sending" : "uploading"}
            platform={channel}
            estimatedTime={isUploading ? estimatedUploadTime : undefined}
            onCancel={isUploading ? onCancelUpload : undefined}
          />
        )}

      {/* Input message row */}
      <div className="d-flex flex-row p-0 m-0 w-100">
        {/* File type selector / attachment button */}
        <div className="d-flex align-items-center me-3">
          <FileTypeSelector
            channel={channel}
            onFileSelect={handleFileSelect}
            disabled={isSending}
            onProductClick={
              canPickProduct ? () => setIsProductPickerOpen(true) : undefined
            }
          />
        </div>

        {/* Marketplace product picker modal. TikTok, Lazada, and Shopee
            go through the unified interactive/product wire on the server. */}
        {canPickProduct && shopId && channel && (
          <ProductPickerModal
            platform={channel as "tiktok" | "shopee" | "lazada" | "whatsapp"}
            shopId={shopId}
            isOpen={isProductPickerOpen}
            onClose={() => setIsProductPickerOpen(false)}
            onSelect={(product) => {
              if (file) onRemoveFile();
              setSelectedProduct(product);
            }}
          />
        )}

        {/* Text input */}
        <div
          className="position-relative bg-white w-100"
          style={{ borderRadius: "10px" }}
        >
          {messageType !== "document" && messageType !== "audio" && (
            <>
              <div>
                <textarea
                  data-testid="chatTextMessage"
                  id="chatTextMessage"
                  className="form-control form-control-flush px-2 py-auto"
                  style={{
                    minHeight: "40px",
                    maxHeight: "120px",
                    backgroundColor: !isCaptionSupported()
                      ? "#f5f5f5"
                      : "white",
                    resize: "none", // Prevent manual resize
                    cursor: !isCaptionSupported() ? "not-allowed" : "text",
                    borderTop: "1px solid #cccccc",
                    borderLeft: "1px solid #cccccc",
                    borderRight: "1px solid #cccccc",
                  }}
                  maxLength={1000}
                  rows={1}
                  data-kt-element="input"
                  placeholder={
                    !isCaptionSupported()
                      ? t("HC.Input.CaptionNotSupported")
                      : t("HC.Input.TextMessage")
                  }
                  title={
                    !isCaptionSupported()
                      ? t("HC.Input.CaptionNotSupportedTooltip")
                      : undefined
                  }
                  disabled={!isCaptionSupported()}
                  value={message}
                  onChange={handleTextChange}
                  onKeyDown={onEnterPress}
                  autoFocus={isCaptionSupported()}
                  ref={chatInputRef}
                />
              </div>
            </>
          )}

          {/* Character counter */}
          {messageType !== "document" && messageType !== "audio" && (
            <CharacterCounter
              message={message}
              maxLength={1000}
              disabled={!isCaptionSupported()}
            />
          )}
        </div>

        {/* Send button */}
        <div className="d-flex align-items-center ms-3">
          <button
            id="sendButton"
            data-testid="send-button"
            ref={sendButtonRef}
            type="button"
            data-kt-element="send"
            className={classNames(
              "btn btn-primary btn-sm m-0 p-0 d-flex align-items-center justify-content-center",
              isMobile && "tw-w-9 tw-h-9"
            )}
            onClick={handleSendClick}
            disabled={isSending}
            style={
              isMobile
                ? {
                    minWidth: "36px",
                    minHeight: "36px",
                    borderRadius: "8px",
                    opacity: isSending ? 0.6 : 1,
                  }
                : {
                    width: "40px",
                    height: "40px",
                    borderRadius: "8px",
                    opacity: isSending ? 0.6 : 1,
                  }
            }
          >
            {isSending ? (
              // Spinner when sending
              <div
                className="spinner-border spinner-border-sm text-white"
                role="status"
                style={{ width: "18px", height: "18px" }}
              >
                <span className="visually-hidden">Sending...</span>
              </div>
            ) : (
              // Send icon when not sending
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width={isMobile ? "18" : "20"}
                height={isMobile ? "18" : "20"}
                viewBox="0 0 14 14"
                className="text-white"
              >
                <path
                  fill="currentColor"
                  fillRule="evenodd"
                  d="M11.821.098a1.62 1.62 0 0 1 2.077 2.076l-3.574 10.712a1.62 1.62 0 0 1-1.168 1.069a1.599 1.599 0 0 1-1.52-.434l-1.918-1.909l-2.014 1.042a.5.5 0 0 1-.73-.457l.083-3.184l7.045-5.117a.625.625 0 1 0-.735-1.012L2.203 8.088l-1.73-1.73a1.6 1.6 0 0 1-.437-1.447a1.62 1.62 0 0 1 1.069-1.238h.003L11.82.097Z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatInputArea;
